"use client";

import { type JSX, useCallback, useMemo, useRef, useState } from "react";
import {
  AppPlaceholder,
  Text,
  useSitecore,
} from "@sitecore-content-sdk/nextjs";

import { AccordionContext } from "contexts/AccordionContext";
import { AccordionProps } from "./accordion.props";

import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";

export const Default = (props: AccordionProps): JSX.Element => {
  console.log("---->[Accordion] Component", props.fields);

  const { page, fields, params, rendering } = props;
  // componentMap from SitecoreProvider context — see Navigation.tsx note;
  // a static `import componentMap from '.sitecore/component-map'` would
  // create a load-time TDZ cycle on registered exports.
  const { componentMap } = useSitecore();

  const isFirstRegistered = useRef(false);
  const phKey = `accordionitem-${rendering?.params?.DynamicPlaceholderId}`;
  const firstItem = rendering?.placeholders?.[phKey]?.[0];
  const dynamicId =
    typeof firstItem === "object" && firstItem !== null && "params" in firstItem
      ? ((firstItem as { params?: { DynamicPlaceholderId?: string } }).params
          ?.DynamicPlaceholderId ?? "0")
      : "0";
  const [activeIndex, setActiveIndex] = useState<number>(+dynamicId);
  const [prevDynamicId, setPrevDynamicId] = useState<string>(dynamicId);

  const registerFirstItem = useCallback((id: number) => {
    if (!isFirstRegistered.current) {
      setActiveIndex(id);
      isFirstRegistered.current = true;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      activeIndex,
      setActiveIndex,
      registerFirstItem,
    }),
    [activeIndex, registerFirstItem],
  );

  if (dynamicId !== prevDynamicId) {
    setPrevDynamicId(dynamicId);
    setActiveIndex(+dynamicId);
  }

  if (!fields) {
    return <NoDataFallback componentName="Accordion" />;
  }

  const id = params?.RenderingIdentifier;
  const mainTitle = fields.AccordionMainTitle;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <AccordionContext.Provider value={contextValue}>
      <div
        className={`accordion-component px-[var(--spacing-sixteen)] mb-[var(--spacing-thirtytwo)] ${hiddenBlockStyles}`}
        id={id || undefined}
        data-testid="accordion-component"
        data-role="accordion-component"
      >
        <div className="component-content">
          {mainTitle && (
            <Text
              tag="h2"
              field={mainTitle}
              className="text-black font-medium text-[28px] leading-[42px] tracking-[0.4px] lg:text-[32px] lg:leading-[40px] lg:tracking-[0.8px] mt-[var(--spacing-thirtytwo)] mb-0"
              data-testid="main-title"
            />
          )}
          <div className="mt-[var(--spacing-thirtytwo)]">
            <AppPlaceholder
              name={phKey}
              rendering={rendering}
              page={page}
              componentMap={componentMap}
            />
          </div>
        </div>
      </div>
    </AccordionContext.Provider>
  );
};
